import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { ApiPost } from "../../helpers/API/ApiData";
import { TableThumbnail } from "../../components/TableThumbnail";
import type { ContentLanguage } from "../../hooks/useContentLanguage";

interface CompanyDraggableListProps {
  rows: any[];
  lang: ContentLanguage;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (row: any) => void;
  onDelete: (row: any) => void;
  onReordered: () => void;
}

export function CompanyDraggableList({
  rows,
  lang,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onReordered,
}: CompanyDraggableListProps) {
  const [localRows, setLocalRows] = useState<any[]>(rows);

  useEffect(() => {
    setLocalRows(rows);
  }, [rows]);

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const reordered = Array.from(localRows);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setLocalRows(reordered);

    const order = reordered.map((row, idx) => ({ _id: row._id, number: idx + 1 }));
    try {
      await ApiPost("company-profile/reorder", { order });
      onReordered();
    } catch (err: any) {
      setLocalRows(rows);
      toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
    }
  };

  if (localRows.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="text.secondary">Keine Unternehmen vorhanden</Typography>
      </Box>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="company-list">
        {(provided) => (
          <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ p: 1 }}>
            {localRows.map((row, index) => (
              <Draggable key={row._id} draggableId={row._id} index={index}>
                {(prov, snapshot) => (
                  <Paper
                    ref={prov.innerRef}
                    {...prov.draggableProps}
                    elevation={snapshot.isDragging ? 4 : 1}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1.5,
                      mb: 1,
                      bgcolor: snapshot.isDragging ? "action.hover" : "background.paper",
                    }}
                  >
                    <Box {...prov.dragHandleProps} sx={{ display: "flex", cursor: "grab", color: "text.secondary" }}>
                      <DragIndicatorIcon />
                    </Box>
                    <TableThumbnail src={lang === "de" ? row?.companyLogo_ger?.[0] : row?.companyLogo?.[0]} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" noWrap>
                        {lang === "de" ? row?.companyName_ger : row?.companyName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                        {lang === "de" ? row?.address_ger : row?.address}
                      </Typography>
                    </Box>
                    {canEdit && (
                      <Tooltip title="Bearbeiten">
                        <IconButton size="small" onClick={() => onEdit(row)}>
                          <CreateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Löschen">
                        <IconButton size="small" onClick={() => onDelete(row)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Paper>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
}
